<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'click-deal', 'dragstart', 'dragend']);

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const formattedValue = computed(() => {
  const val = Number(props.deal.value) || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.deal.currency || 'BRL',
  }).format(val);
});

const formattedDate = computed(() => {
  if (!props.deal.expected_close_date) return null;
  const [year, month, day] = props.deal.expected_close_date.split('-');
  return `${day}/${month}/${year}`;
});

const onDragStart = event => {
  event.dataTransfer.setData(
    'text/plain',
    JSON.stringify({ dealId: props.deal.id, fromStageId: props.deal.pipeline_stage_id })
  );
  event.dataTransfer.effectAllowed = 'move';
  emit('dragstart', props.deal);
};

const onDragEnd = () => {
  emit('dragend', props.deal);
};

const onCardClick = () => {
  emit('click-deal', props.deal);
};

const onEditClick = e => {
  e.stopPropagation();
  emit('edit', props.deal);
};
</script>

<template>
  <div
    class="p-3 mb-2 bg-n-surface-1 rounded-xl border border-n-weak hover:border-brand/40 shadow-xs hover:shadow-sm transition-all cursor-pointer select-none group relative"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="onCardClick"
  >
    <!-- Top Row: Title & Value -->
    <div class="flex items-start justify-between gap-1.5 mb-1.5">
      <h4 class="font-medium text-xs text-n-slate-12 group-hover:text-n-brand line-clamp-2 leading-snug transition-colors">
        {{ deal.title }}
      </h4>
      <span class="text-[11px] font-semibold text-emerald-500 whitespace-nowrap bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded leading-none flex-shrink-0 font-mono">
        {{ formattedValue }}
      </span>
    </div>

    <!-- Contact & Conversation info -->
    <div v-if="deal.contact || deal.conversation_id" class="flex flex-col gap-1 mb-2.5 text-[11px] text-n-slate-11">
      <div v-if="deal.contact" class="flex items-center gap-1.5 truncate">
        <svg class="w-3 h-3 text-n-slate-10 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span class="truncate text-n-slate-12 font-medium">
          {{ deal.contact.name || deal.contact.phone_number || deal.contact.email }}
        </span>
      </div>

      <div v-if="deal.conversation_id" class="flex items-center gap-1.5 text-n-brand font-medium">
        <svg class="w-3 h-3 flex-shrink-0 text-n-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span>Chat ativo</span>
      </div>
    </div>

    <!-- Bottom Row: Assignee & Actions -->
    <div class="flex items-center justify-between pt-2 border-t border-n-weak text-[11px] text-n-slate-10">
      <div class="flex items-center gap-1.5">
        <div
          v-if="deal.user"
          class="w-4.5 h-4.5 rounded-full bg-brand-subtle text-n-brand flex items-center justify-center text-[9px] font-semibold"
          :title="deal.user.name"
        >
          {{ deal.user.name.charAt(0).toUpperCase() }}
        </div>
        <span v-if="deal.user" class="truncate max-w-[85px] text-n-slate-11">
          {{ deal.user.name }}
        </span>
        <span v-else class="italic text-n-slate-10 text-[10px]">Sem responsável</span>
      </div>

      <div class="flex items-center gap-1.5">
        <span v-if="formattedDate" class="text-[10px] text-n-slate-10 flex items-center gap-0.5">
          <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {{ formattedDate }}
        </span>

        <button
          type="button"
          class="p-1 text-n-slate-10 hover:text-n-slate-12 rounded hover:bg-n-alpha-2 transition-colors cursor-pointer"
          title="Editar Dados"
          @click="onEditClick"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
